import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Star } from 'lucide-react';
import { toast } from 'sonner';

export default function RatingModal({ rideId, participants, onClose }) {
  const [ratings, setRatings] = useState(
    participants.reduce((acc, p) => ({ ...acc, [p.id]: { stars: 0, comment: '' } }), {})
  );
  const [loading, setLoading] = useState(false);
  const [currentParticipantIndex, setCurrentParticipantIndex] = useState(0);

  const currentParticipant = participants[currentParticipantIndex];
  const currentRating = ratings[currentParticipant?.id] || { stars: 0, comment: '' };

  const handleStarClick = (stars) => {
    setRatings({
      ...ratings,
      [currentParticipant.id]: { ...currentRating, stars },
    });
  };

  const handleCommentChange = (e) => {
    setRatings({
      ...ratings,
      [currentParticipant.id]: { ...currentRating, comment: e.target.value },
    });
  };

  const handleNext = async () => {
    if (currentRating.stars === 0) {
      toast.error('Please select a rating');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`/ratings/${rideId}`, {
        rated_user_id: currentParticipant.id,
        stars: currentRating.stars,
        comment: currentRating.comment,
      });

      if (currentParticipantIndex < participants.length - 1) {
        setCurrentParticipantIndex(currentParticipantIndex + 1);
      } else {
        toast.success('Ratings submitted successfully!');
        onClose();
      }
    } catch (error) {
      if (error.response?.data?.detail?.includes('already rated')) {
        // Skip to next if already rated
        if (currentParticipantIndex < participants.length - 1) {
          setCurrentParticipantIndex(currentParticipantIndex + 1);
        } else {
          toast.info('All ratings completed');
          onClose();
        }
      } else {
        toast.error('Failed to submit rating');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!currentParticipant) {
    return null;
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="rating-modal">
        <DialogHeader>
          <DialogTitle>
            Rate {currentParticipant.name} ({currentParticipantIndex + 1}/{participants.length})
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          {/* Participant Info */}
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-xl">
                {currentParticipant.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-white font-semibold" data-testid="rating-participant-name">{currentParticipant.name}</p>
              <p className="text-sm text-gray-400">{currentParticipant.experience_level}</p>
              <div className="flex items-center gap-1 mt-1">
                <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                <span className="text-sm text-gray-400">
                  {currentParticipant.avg_rating.toFixed(1)} ({currentParticipant.total_ratings} ratings)
                </span>
              </div>
            </div>
          </div>

          {/* Star Rating */}
          <div>
            <p className="text-white font-medium mb-3">How was your ride with {currentParticipant.name}?</p>
            <div className="flex gap-2 justify-center" data-testid="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleStarClick(star)}
                  className="transition-transform hover:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-10 h-10 ${
                      star <= currentRating.stars
                        ? 'text-yellow-500 fill-yellow-500'
                        : 'text-gray-600'
                    }`}
                  />
                </button>
              ))}
            </div>
            {currentRating.stars > 0 && (
              <p className="text-center text-gray-400 mt-2">
                {currentRating.stars === 1 && 'Poor experience'}
                {currentRating.stars === 2 && 'Below average'}
                {currentRating.stars === 3 && 'Good'}
                {currentRating.stars === 4 && 'Great ride!'}
                {currentRating.stars === 5 && 'Excellent partner!'}
              </p>
            )}
          </div>

          {/* Comment */}
          <div>
            <label className="text-white font-medium block mb-2">
              Add a comment (optional)
            </label>
            <Textarea
              value={currentRating.comment}
              onChange={handleCommentChange}
              placeholder="Share your experience..."
              rows={3}
              data-testid="rating-comment"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleNext}
              disabled={loading || currentRating.stars === 0}
              className="flex-1"
              data-testid="submit-rating"
            >
              {loading
                ? 'Submitting...'
                : currentParticipantIndex < participants.length - 1
                ? 'Next'
                : 'Finish'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
